import { isPlainObject } from "lodash";

/**
 * SchemaForm inference: plain-JSON-Schema -> form-element dialect.
 *
 * The {@link SchemaForm} renderer switches on each node's `element` key
 * (`form-object`, `form-string`, `form-integer`, ...). Handed a *plain*
 * JSON Schema (`type`/`properties`, no `element` keys) it used to render a bare
 * "Unknown form element" callout with zero input fields.
 *
 * A flow's `input_schema` is also what the MCP surface exposes as a tool
 * `inputSchema` (plain JSON Schema), so authors naturally write JSON Schema and
 * NOT the form-element dialect. This module makes those schemas render as real
 * forms by inferring an `element` for every node that lacks one, mapping JSON
 * Schema `type` (+ `format`, nullability) to the corresponding dialect element.
 *
 * Conservative by construction:
 * - A schema whose ROOT already carries `element` is the form-element dialect and
 *   is returned completely UNTOUCHED — connector configs, custom-object attribute
 *   schemas and wrapper schemas render byte-identically to before.
 * - An empty (`{}`) or non-object schema is returned unchanged (so the SchemaForm
 *   "no schema" / code-view fallback keeps working).
 * - Only genuinely element-less trees are transformed, and only `element`/`label`
 *   are ADDED — every original key (`type`, `default`, `description`, `required`,
 *   `enum`, ...) is preserved, so the value semantics submitted by the form are
 *   unchanged.
 *
 * A scalar `enum` (a closed set of allowed values) is enriched into a value-based
 * dropdown (see `constElementFor`), value-preserving: the submitted data is the
 * chosen enum member, not an index.
 *
 * A JSON-Schema `number` (floating-point) maps to `form-number`, a real numeric
 * input that accepts decimals (its integer sibling is `form-integer`).
 *
 * Types with no faithful dialect element (`null`, `oneOf`/`anyOf`/`allOf`/`$ref`,
 * or an opaque object with no shape) fall back to `form-json` — a per-field
 * YAML/code editor — instead of "Unknown form element". That keeps the field
 * type-correct (YAML parses the value back to its type) while still being
 * editable.
 */

const NULL_TYPE = "null";

/** Map a JSON-Schema string `format` to its dialect element suffix (base name). */
const STRING_FORMAT_BASE: Record<string, string> = {
    "date-time": "datetime",
    date: "date",
    time: "time",
};

/**
 * Determine the primary (non-null) JSON-Schema type of a node and whether the
 * node is nullable. Handles both `type: "string"` and `type: ["string", "null"]`
 * (and the OpenAPI-style `nullable: true`).
 */
const analyzeType = (node: any): { baseType: string | undefined; nullable: boolean } => {
    let type = node.type;
    let nullable = node.nullable === true;
    if (Array.isArray(type)) {
        nullable = nullable || type.includes(NULL_TYPE);
        const nonNull = type.filter((entry: unknown) => entry !== NULL_TYPE);
        type = nonNull[0];
    }
    return { baseType: typeof type === "string" ? type : undefined, nullable };
};

/** `form-<base>` or, when nullable, `form-optional-<base>`. */
const elementFor = (base: string, nullable: boolean) =>
    nullable ? `form-optional-${base}` : `form-${base}`;

/**
 * The `form-const-<base>` element carrying a single fixed enum member value.
 * The base type only affects seeding of `null` (`form-const-null` → `null`); for
 * every other concrete value the const element resolves to its `const` (see
 * getDefaultRequiredValue), so the exact non-null base is cosmetic. All
 * `form-const-*` elements render as an empty field — the dropdown option label is
 * the visible control, the branch itself shows nothing.
 */
const constElementFor = (value: unknown): string => {
    if (value === null) return "form-const-null";
    switch (typeof value) {
        case "number":
            return "form-const-integer";
        case "boolean":
            return "form-const-boolean";
        default:
            return "form-const-string";
    }
};

/**
 * Recursively infer the form-element dialect for a single JSON-Schema node.
 * `key` is the property name the node sits under in its parent object; it is used
 * as the field label (a node's own `title` wins over the key).
 */
const inferNode = (node: any, key?: string): any => {
    // Non-object schema fragments (e.g. a boolean JSON Schema) can't be enriched.
    if (!isPlainObject(node)) {
        return node;
    }
    // Respect an explicit dialect element if one is already present (defensive:
    // handles a partially-authored / mixed schema without clobbering it).
    if (typeof node.element === "string") {
        return node;
    }

    const label = node.title ?? key;
    const withMeta = (extra: Record<string, unknown>) => ({
        ...node,
        ...(label !== undefined ? { label } : {}),
        ...extra,
    });

    const { baseType, nullable } = analyzeType(node);

    // A constrained `enum` (a fixed, closed set of allowed values) renders as a
    // dropdown. We map it onto the existing (oneOf/anyOf-based) form-dropdown,
    // giving each enum member its own `const` branch. This is VALUE-preserving:
    // the dropdown submits the selected branch's data — a `form-const-*` branch
    // resolves to its `const` (see getDefaultRequiredValue) — NOT the option
    // index, so the flow input still receives the enum member itself. Only scalar
    // enums are enriched; an object/array-typed node keeps its structural
    // rendering even if it also carries an `enum`.
    const enumValues = Array.isArray(node.enum) ? node.enum : undefined;
    const isStructural =
        baseType === "object" ||
        baseType === "array" ||
        isPlainObject(node.properties) ||
        node.items !== undefined ||
        node.additionalProperties !== undefined;
    if (enumValues && enumValues.length > 0 && !isStructural) {
        const anyOf = enumValues.map((value: unknown) => ({
            const: value,
            label: String(value),
            element: constElementFor(value),
        }));
        const matchedIndex =
            node.default !== undefined
                ? enumValues.findIndex((value: unknown) => value === node.default)
                : -1;
        // Emit the dialect's CANONICAL dropdown shape: the `Form element: dropdown`
        // branch of formSchema.json requires `element` + `anyOf` (the choices) +
        // `defaultIndex`. Emitting `oneOf` or omitting `defaultIndex` matches no
        // builder branch, so the EditableSchemaForm element-type picker resolves to
        // index -1 and renders "Invalid choice" (author-time regression). `anyOf`
        // renders identically to `oneOf` — SchemaFormDropdown reads `oneOf ?? anyOf`
        // — and `defaultIndex` defaults to 0 (the first choice) as a builder-authored
        // dropdown does, so a plain enum round-trips cleanly through the builder.
        return withMeta({
            element: elementFor("dropdown", nullable),
            anyOf,
            defaultIndex: matchedIndex >= 0 ? matchedIndex : 0,
        });
    }

    const isObjectNode =
        baseType === "object" ||
        isPlainObject(node.properties) ||
        isPlainObject(node.patternProperties) ||
        node.additionalProperties !== undefined;

    if (isObjectNode) {
        const hasProperties =
            isPlainObject(node.properties) && Object.keys(node.properties).length > 0;
        const hasDynamicProperties =
            isPlainObject(node.patternProperties) ||
            node.additionalProperties === true ||
            isPlainObject(node.additionalProperties);
        if (!hasProperties && !hasDynamicProperties) {
            // An object with no declared shape has nothing to render as a form.
            return withMeta({ element: "form-json" });
        }
        const properties = hasProperties
            ? Object.fromEntries(
                  Object.entries(node.properties).map(([childKey, childNode]) => [
                      childKey,
                      inferNode(childNode, childKey),
                  ])
              )
            : node.properties;
        const additionalProperties = isPlainObject(node.additionalProperties)
            ? inferNode(node.additionalProperties)
            : node.additionalProperties;
        return withMeta({
            element: elementFor("object", nullable),
            ...(properties !== undefined ? { properties } : {}),
            ...(node.additionalProperties !== undefined ? { additionalProperties } : {}),
        });
    }

    const isArrayNode = baseType === "array" || node.items !== undefined;
    if (isArrayNode) {
        const items = isPlainObject(node.items) ? inferNode(node.items) : node.items;
        return withMeta({
            element: elementFor("array", nullable),
            ...(node.items !== undefined ? { items } : {}),
        });
    }

    if (baseType === "string") {
        const formatBase =
            typeof node.format === "string" ? STRING_FORMAT_BASE[node.format] : undefined;
        return withMeta({ element: elementFor(formatBase ?? "string", nullable) });
    }
    if (baseType === "integer") {
        return withMeta({ element: elementFor("integer", nullable) });
    }
    if (baseType === "number") {
        return withMeta({ element: elementFor("number", nullable) });
    }
    if (baseType === "boolean") {
        return withMeta({ element: elementFor("boolean", nullable) });
    }

    // null, enum-without-type, oneOf/anyOf/allOf/$ref, or otherwise
    // unmapped -> graceful per-field code editor (never "Unknown form element").
    return withMeta({ element: "form-json" });
};

/**
 * Normalize a schema for the SchemaForm renderer: enrich a plain JSON Schema into
 * the form-element dialect, or return it unchanged when it is already the dialect
 * (root has `element`), empty, or not an object. See the module doc-comment.
 */
export const normalizeSchemaForForm = (schema: any): any => {
    if (!isPlainObject(schema) || Object.keys(schema).length === 0) {
        return schema;
    }
    if (typeof schema.element === "string") {
        return schema;
    }
    return inferNode(schema);
};
